/**
 * A line-level diff, computed with a plain LCS. Enough to show a description
 * that changed after approval, which is the only diff this app ever renders.
 */

type Op = { kind: "same" | "add" | "del"; text: string };

function diffLines(before: string, after: string): Op[] {
  const a = before.split("\n");
  const b = after.split("\n");

  // LCS table. These strings are a few dozen lines; O(n·m) is fine.
  const lcs: number[][] = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      lcs[i]![j] = a[i] === b[j] ? lcs[i + 1]![j + 1]! + 1 : Math.max(lcs[i + 1]![j]!, lcs[i]![j + 1]!);
    }
  }

  const ops: Op[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      ops.push({ kind: "same", text: a[i]! });
      i++;
      j++;
    } else if (lcs[i + 1]![j]! >= lcs[i]![j + 1]!) {
      ops.push({ kind: "del", text: a[i]! });
      i++;
    } else {
      ops.push({ kind: "add", text: b[j]! });
      j++;
    }
  }
  while (i < a.length) ops.push({ kind: "del", text: a[i++]! });
  while (j < b.length) ops.push({ kind: "add", text: b[j++]! });
  return ops;
}

export function DiffView({ before, after }: { before: string; after: string }) {
  const ops = diffLines(before, after);

  return (
    <pre className="wire-scroll max-h-80 overflow-auto rounded-medium border border-default-100/60 bg-default-50/30 p-2 font-mono text-[11px] leading-relaxed">
      {ops.map((op, i) => (
        <div
          key={i}
          className={
            op.kind === "add"
              ? "bg-success/15 text-success"
              : op.kind === "del"
                ? "bg-danger/15 text-danger line-through decoration-danger/40"
                : "text-default-400"
          }
        >
          <span className="mr-2 inline-block w-3 select-none text-default-600">
            {op.kind === "add" ? "+" : op.kind === "del" ? "−" : " "}
          </span>
          {op.text || " "}
        </div>
      ))}
    </pre>
  );
}
